import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { AtSign, Bookmark, FileQuestion, Hash, MessageSquare, Trash2, Users, Volume2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { channelName, useSocialStore } from '@/store/social'
import { savedApi, type SavedEntry } from '@/lib/savedApi'
import { applySavedEvent, savedEntryVisible, unavailableSavedEntry } from '@/lib/savedMessages'
import { describeError } from '@/lib/auth'
import { formatDateTime } from '@/lib/format'
import { openChannelLink } from '@/lib/navigate'
import { ChannelType } from '@/types/api'
import { cx } from '@/lib/cx'
import { MobileBack } from '../MobileBack'
import { MessagePreview } from './MessagePreview'
import chat from '../ChatView.module.css'
import cards from './InboxPage.module.css'
import styles from './SavedMessagesPage.module.css'

export function SavedMessagesPage() {
  const me = useAuthStore((s) => s.user)
  const ready = useAppStore((s) => s.gateway === 'ready')
  const guilds = useGuildStore((s) => s.guilds)
  const channels = useGuildStore((s) => s.channels)
  const members = useGuildStore((s) => s.members)
  const privateChannels = useSocialStore((s) => s.channels)
  const relationships = useSocialStore((s) => s.relationships)
  const client = useQueryClient()
  const feed = useInfiniteQuery({
    queryKey: ['saved-messages', me?.id],
    queryFn: ({ pageParam }) => savedApi.list(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.has_more ? page.next_before ?? undefined : undefined,
    enabled: Boolean(me), staleTime: 10_000, refetchInterval: 30_000,
  })
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const refresh = () => { if (!timer) timer = setTimeout(() => { timer = undefined; void client.invalidateQueries({ queryKey: ['saved-messages', me?.id] }) }, 500) }
    const social = useSocialStore.subscribe((s, prev) => { if (s.channels !== prev.channels || s.relationships !== prev.relationships) refresh() })
    const guild = useGuildStore.subscribe((s, prev) => { if (s.guilds !== prev.guilds || s.channels !== prev.channels || s.members !== prev.members) refresh() })
    return () => { social(); guild(); clearTimeout(timer) }
  }, [client, me?.id])
  const seen = new Set<string>()
  const items = (feed.data?.pages.flatMap((p) => p.items) ?? []).filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true })
  return (
    <main className={chat.chat} aria-label="Сохранённое">
      <header className={chat.header}>
        <MobileBack label="К списку бесед" /><Bookmark size={24} className={chat.hash} /><h1 className={chat.title}>Сохранённое</h1>
      </header>
      <div className={cx(cards.feed, 'scroller')}><div className={cards.inner}>
        {feed.isPending ? <p className={cards.empty} role="status">Загружаем сохранённое…</p> : null}
        {feed.isError ? <div className={cards.empty}><p role="alert">{describeError(feed.error).message}</p><button type="button" className={cards.retry} onClick={() => void feed.refetch()}>Повторить</button></div> : null}
        {!feed.isPending && !feed.isError && !items.length && !feed.hasNextPage ? <div className={cards.empty}><Bookmark size={36} aria-hidden="true" /><h2>Пока ничего нет</h2><p>Сохраните сообщение через меню «…».</p></div> : null}
        {items.map((e) => <SavedCard key={e.id} entry={me && savedEntryVisible(e, { userId: me.id, ready, guilds, channels, members, privateChannels, relationships }) ? e : unavailableSavedEntry(e)} />)}
        {feed.hasNextPage ? <button type="button" className={cards.more} disabled={feed.isFetching} onClick={() => void feed.fetchNextPage()}>{feed.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}</button> : null}
      </div></div>
    </main>
  )
}

function SavedCard({ entry: e }: { entry: SavedEntry }) {
  const userId = useAuthStore((s) => s.user?.id)
  const client = useQueryClient()
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)
  const ch = e.channel
  const title = ch ? ch.guild_id ? ch.name ?? 'Канал' : channelName(ch) : 'Сообщение недоступно'
  const Icon = !ch ? FileQuestion : ch.type === ChannelType.GuildVoice ? Volume2 : ch.type === ChannelType.PublicThread ? MessageSquare : ch.guild_id ? Hash : ch.type === ChannelType.GroupDM ? Users : AtSign
  const open = () => { if (ch && e.available) void openChannelLink({ guildId: ch.guild_id ?? null, channelId: ch.id, messageId: e.message_id }) }
  const remove = async () => {
    if (busy.current || !userId) return
    busy.current = true; setRemoving(true); setError(null)
    try {
      await savedApi.remove(e.channel_id, e.message_id)
      applySavedEvent(client, userId, 'SAVED_MESSAGE_DELETE', { channel_id: e.channel_id, message_id: e.message_id })
    } catch (err) { setError(describeError(err).message) }
    finally { busy.current = false; setRemoving(false) }
  }
  return <section className={cards.card} aria-label={title}>
    <div className={cards.cardHeader}>
      {e.available ? <button type="button" className={cards.channel} onClick={open}><Icon size={20} /><span><strong>{title}</strong><span className={cards.source}>{e.guild_name || (ch?.type === ChannelType.GroupDM ? 'Групповая беседа' : 'Личные сообщения')}</span></span></button> : <div className={cards.channel}><Icon size={20} /><strong>{title}</strong></div>}
      <button type="button" className={cards.mark} aria-label="Убрать из сохранённого" data-tooltip="Убрать из сохранённого" disabled={removing} onClick={() => void remove()}><Trash2 size={18} /></button>
    </div>
    {e.available && e.message ? <MessagePreview message={e.message} onOpen={open} /> : <p className={styles.unavailable}>Сообщение удалено или у вас нет доступа к переписке.</p>}
    <div className={styles.footer}><span>Сохранено {formatDateTime(new Date(e.saved_at)).toLowerCase()}</span>{e.available ? <button type="button" onClick={open}>Перейти к сообщению</button> : null}</div>
    {error ? <p className={cards.error} role="alert">{error}</p> : null}
  </section>
}
